import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCalendarAlt, FaUser, FaArrowRight } from 'react-icons/fa';
import { blogPosts, blogCategories } from '../data/blogData';
import { useApiData } from '../hooks/useApiData';
import { fetchBlogs, fetchImage } from '../repository/api';
import { formatExcerpt, formatBlogContent } from '../utils/textFormatter';

const BlogSection = () => {
  // Fetch blogs from API
  const { data: blogsData, loading, error } = useApiData(() => fetchBlogs({ per_page: 20 }));

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) console.log('Blog API Data received:', apiData);
    
    if (!apiData || !apiData.blogs || !Array.isArray(apiData.blogs) || apiData.blogs.length === 0) {
      if (isDebug) console.log('No valid blogs data found, using default data');
      return blogPosts;
    }
    
    return apiData.blogs.map((blog) => ({
      id: blog.id,
      title: blog.title || 'Untitled Blog Post',
      excerpt: formatExcerpt(blog.paragraph_explanation || 'No description available'),
      category: blog.category?.toLowerCase() || 'general',
      date: new Date(blog.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      author: blog.author_name || 'Anonymous',
      authorImage: null,
      authorBio: '',
      image: blog.image_path ? fetchImage(blog.image_path) : blogPosts[0].image, // Fallback to first default image
      featured: false,
      tags: blog.tags || [],
      content: blog.sections ? formatBlogContent(blog.sections) : ''
    }));
  };

  // Create mock API data based on the structure you provided
  const createMockApiData = () => {
    return {
      "blogs": [
        {
          "id": "mock-blog-1",
          "title": "Why USSD is Good for Business",
          "paragraph_explanation": "USSD technology has revolutionized how businesses interact with customers in emerging markets. This simple yet powerful technology offers numerous advantages for businesses looking to improve customer engagement.",
          "category": "Technology",
          "author_name": "John Doe",
          "image_path": "ussd-blog-image.jpg",
          "tags": ["USSD", "Technology", "Business", "Mobile"],
          "sections": [
            {
              "header": "Why USSD is Good",
              "explanation": "USSD offers several advantages for businesses:\n1. Real-time interaction\n2. No internet required\n3. Wide device compatibility\n4. Cost-effective solution"
            }
          ],
          "created_at": "2024-01-01T00:00:00",
          "updated_at": "2024-01-01T00:00:00"
        },
        {
          "id": "mock-blog-2",
          "title": "Bulk SMS Marketing Strategies",
          "paragraph_explanation": "Learn how to create effective bulk SMS marketing campaigns that drive engagement and increase customer retention.",
          "category": "Marketing",
          "author_name": "Jane Smith",
          "image_path": "sms-marketing-image.jpg",
          "tags": ["SMS", "Marketing", "Engagement", "Strategy"],
          "sections": [
            {
              "header": "SMS Marketing Benefits",
              "explanation": "SMS marketing offers:\n• High open rates\n• Immediate delivery\n• Cost effectiveness\n• Wide reach"
            }
          ],
          "created_at": "2024-01-02T00:00:00",
          "updated_at": "2024-01-02T00:00:00"
        }
      ],
      "pagination": {
        "page": 1,
        "per_page": 20,
        "total": 2,
        "pages": 1
      }
    };
  };

  // Use API data or fallback to default - always ensure we have blogs
  const allBlogs = useMemo(() => {
    if (blogsData && !loading && !error) {
      const transformed = transformApiData(blogsData);
      return transformed.length > 0 ? transformed : blogPosts;
    }
    
    // If API failed but we have error, try using mock data
    if (error && !loading) {
      const isDebug = process.env.NODE_ENV === 'development';
      if (isDebug) console.log('Blog API failed, trying mock data...');
      const mockData = createMockApiData();
      const transformed = transformApiData(mockData);
      if (transformed.length > 0) {
        if (isDebug) console.log('Using mock blog data as fallback');
        return transformed;
      }
    }
    
    return blogPosts;
  }, [blogsData, loading, error]);

  // Randomize and get 6 posts for homepage display
  const homepagePosts = useMemo(() => {
    const shuffled = [...allBlogs].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 6);
  }, [allBlogs]);

  // Debug logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('BlogSection component state:', {
        loading,
        error,
        hasApiData: !!blogsData,
        allBlogsCount: allBlogs.length,
        homepagePostsCount: homepagePosts.length,
        apiData: blogsData
      });
    }
  }, [loading, error, blogsData, allBlogs.length, homepagePosts.length]);

  // Show error state if API call failed (but continue with fallback data)
  if (error) {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) {
      console.error('Blog section error:', error);
      console.log('Using fallback blog data due to API error');
    }
  }

  return (
    <Section id="blog">
      <Container>
        <SectionHeader>
          <SectionTitle>Our <HighlightText>Blog</HighlightText></SectionTitle>
          <SectionDescription>
            Insights, tips, and updates from our team of communication experts
          </SectionDescription>
        </SectionHeader>

        <BlogGrid>
          {homepagePosts.map((post) => (
            <BlogPost key={post.id}>
              <BlogPostImageWrapper>
                <BlogPostImage src={post.image} alt={post.title} />
                <BlogPostCategory>{blogCategories.find(cat => cat.id === post.category)?.name}</BlogPostCategory>
              </BlogPostImageWrapper>
              
              <BlogPostContent>
                <BlogPostMeta>
                  <MetaItem>
                    <FaCalendarAlt />
                    <span>{post.date}</span>
                  </MetaItem>
                  <MetaItem>
                    <FaUser />
                    <span>{post.author}</span>
                  </MetaItem>
                </BlogPostMeta>
                
                <BlogPostTitle>{post.title}</BlogPostTitle>
                <BlogPostExcerpt dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                
                <ReadMoreLink href={`/blog/${post.id}`}>
                  Read more <FaArrowRight />
                </ReadMoreLink>
              </BlogPostContent>
            </BlogPost>
          ))}
        </BlogGrid>
        
        <ViewAllButton href="/blog">View All Articles</ViewAllButton>
      </Container>
    </Section>
  );
};

export default BlogSection;

// === Animations ===
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// === Styled Components ===
const Section = styled.section`
  padding: 100px 40px;
  position: relative;
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%);
  
  @media (max-width: 992px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 60px 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Blog Grid
const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  width: 100%;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const BlogPost = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const BlogPostImageWrapper = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const BlogPostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${BlogPost}:hover & {
    transform: scale(1.05);
  }
`;

const BlogPostCategory = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlogPostContent = styled.div`
  padding: 25px;
`;

const BlogPostMeta = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: #777;
  font-size: 14px;
  
  svg {
    margin-right: 6px;
    color: #2090B0;
  }
`;

const BlogPostTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  line-height: 1.4;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BlogPostExcerpt = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  color: #2090B0;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 6px;
    font-size: 12px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #105090;
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const ViewAllButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 30px;
  margin-top: 40px;
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;